import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Users, ScrollText, CheckCircle2, MessageCircle, Send, Clock } from "lucide-react";

const ADMIN_EMAIL = "bradharris0511@gmail.com";

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return navigate("/", { replace: true });
    if ((user.email || "").toLowerCase() !== ADMIN_EMAIL) {
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {
      toast.error("Couldn't load admin stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && (user.email || "").toLowerCase() === ADMIN_EMAIL) fetchStats();
  }, [user, fetchStats]);

  const sendDigest = async () => {
    setSending(true);
    try {
      await api.post("/admin/digest/send");
      toast.success("Digest sent to your inbox");
    } catch {
      toast.error("Couldn't send digest — check server logs");
    } finally {
      setSending(false);
    }
  };

  if (authLoading || !user || (user.email || "").toLowerCase() !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-[#9B8BAF]" />
      </div>
    );
  }

  return (
    <div className="grain-bg min-h-screen">
      <Navbar />
      <main className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="admin-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#75737A]">Master console</div>
            <h1 className="mt-2 text-4xl sm:text-5xl text-[#2C2A2E]" data-testid="admin-title">
              A quiet look at the app
            </h1>
            <p className="mt-3 text-[#75737A]">
              A private view for you alone — never shown to other users.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full border-[#D4C7E1]" onClick={fetchStats} disabled={loading} data-testid="admin-refresh">
              <Clock className="h-4 w-4 mr-1.5" /> Refresh
            </Button>
            <Button className="btn-primary rounded-full" onClick={sendDigest} disabled={sending} data-testid="admin-send-digest">
              <Send className="h-4 w-4 mr-1.5" /> {sending ? "Sending…" : "Send weekly digest"}
            </Button>
          </div>
        </div>

        {loading || !stats ? (
          <div className="py-24 flex items-center justify-center text-[#75737A]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            {/* Last 7 days */}
            <section className="mt-10">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#75737A] mb-4">Last 7 days</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                <StatCard icon={Users} label="New users" value={stats.last_7_days.new_users} accent="#9B8BAF" />
                <StatCard icon={ScrollText} label="New prayers" value={stats.last_7_days.new_prayers} accent="#9B8BAF" />
                <StatCard icon={CheckCircle2} label="Answered" value={stats.last_7_days.answered} accent="#92A796" />
                <StatCard icon={MessageCircle} label="Contact messages" value={stats.last_7_days.contact_messages} accent="#9B8BAF" />
              </div>
            </section>

            {/* All-time */}
            <section className="mt-12">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#75737A] mb-4">All-time</div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                <StatCard label="Users" value={stats.totals.users} />
                <StatCard label="Active prayers" value={stats.totals.active_prayers} />
                <StatCard label="Answered" value={stats.totals.answered_prayers} accent="#92A796" />
                <StatCard label="Sessions" value={stats.totals.sessions} />
              </div>
            </section>

            {/* Recent users + contact */}
            <section className="mt-12 grid md:grid-cols-2 gap-6">
              <ListCard title="Newest users">
                {stats.latest_users.length === 0 ? (
                  <li className="text-sm text-[#75737A]">None yet.</li>
                ) : stats.latest_users.map((u) => (
                  <li key={u.email} className="flex items-center justify-between text-sm py-1.5">
                    <span className="text-[#2C2A2E] truncate">{u.name || u.email}</span>
                    <span className="text-[#75737A] text-xs whitespace-nowrap">{new Date(u.created_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ListCard>
              <ListCard title="Recent contact messages">
                {stats.latest_contact.length === 0 ? (
                  <li className="text-sm text-[#75737A]">None yet.</li>
                ) : stats.latest_contact.map((c, i) => (
                  <li key={i} className="flex items-center justify-between text-sm py-1.5">
                    <span className="text-[#2C2A2E] truncate">
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[#9B8BAF] mr-2">{c.category}</span>
                      {c.name}
                    </span>
                    <span className="text-[#75737A] text-xs whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ListCard>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent = "#2C2A2E" }) {
  return (
    <div className="prayer-card p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#75737A]">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-[#9B8BAF]" />}
      </div>
      <div className="mt-3 text-3xl" style={{ fontFamily: "'Playfair Display', serif", color: accent }}>
        {value}
      </div>
    </div>
  );
}

function ListCard({ title, children }) {
  return (
    <div className="prayer-card p-6">
      <h3 className="text-lg text-[#2C2A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
      <ul className="mt-3 divide-y divide-[#EFEBF4]">{children}</ul>
    </div>
  );
}
